import { Reducer, Action } from 'redux';
import { fork, call, takeEvery, put, all, select, delay, putResolve } from 'redux-saga/effects';

import { actionCreators as layoutActionCreators } from '../reducers/Layout';
import { selectors as authSelectors } from '../reducers/Auth';
import LeaseClauseModel from 'src/models/LeaseClause';
import Api from '../api';
import ContractAddendumClause from 'src/components/project/sections/LeasingComponents/redesign/ContractGenerationModels/ContractAddendumClause';
import ErrorResponse from 'src/models/ErrorResponse';

// tslint:disable no-implicit-any

// Store
export interface ILeaseClauseState {
    clauses: Array<LeaseClauseModel>;
    contractClauses: Array<ContractAddendumClause>;
    error: string;
    errorCode: string;
}

const initialState: ILeaseClauseState = {
    clauses: undefined,
    contractClauses: undefined,
    error: undefined,
    errorCode: undefined
};

// Action Types
export enum actionTypes {
    DELETE_LEASE_CLAUSE = '[clause] DELETE_LEASE_CLAUSE',
    DELETE_LEASE_CLAUSE_ERROR = '[clause] DELETE_LEASE_CLAUSE_ERROR',
    GET_CONTRACT_CLAUSES = '[clause] GET_CONTRACT_CLAUSES',
    GET_CONTRACT_CLAUSES_SUCCESS = '[clause] GET_CONTRACT_CLAUSES_SUCCESS',
    CREATE_CONTRACT_CLAUSE = '[clause] CREATE_CONTRACT_CLAUSE',
    CREATE_CONTRACT_CLAUSE_SUCCESS = '[clause] CREATE_CONTRACT_CLAUSE_SUCCESS',
    UPDATE_CONTRACT_CLAUSE = '[clause] UPDATE_CONTRACT_CLAUSE',
    CLEAR_CLAUSES = '[clause] CLEAR_CLAUSES',
    CLEAR_ERROR = '[clause] CLEAR_ERROR'
}

// Action Creator
export const actionCreators = {
    deleteLeaseClause: (clientNumber: number, projectId: number, id: number, tractType: string) => {
        return { type: actionTypes.DELETE_LEASE_CLAUSE, payload: { clientNumber, projectId, id, tractType } };
    },
    deleteLeaseClauseError: (payload: ErrorResponse) => {
        return { type: actionTypes.DELETE_LEASE_CLAUSE_ERROR, payload: payload };
    },
    getContractClauses: (clientNumber: number, projectId: number) => {
        return { type: actionTypes.GET_CONTRACT_CLAUSES, payload: { clientNumber, projectId } };
    },
    getContractClausesSuccess: (clauses: Array<LeaseClauseModel>) => {
        return { type: actionTypes.GET_CONTRACT_CLAUSES_SUCCESS, payload: clauses };
    },
    createContractClause: (clientNumber: number, projectId: number, clause: ContractAddendumClause) => {
        return { type: actionTypes.CREATE_CONTRACT_CLAUSE, payload: { clientNumber, projectId, clause } };
    },
    createContractClauseSuccess: () => {
        return { type: actionTypes.CREATE_CONTRACT_CLAUSE_SUCCESS };
    },
    updateContractClause: (clientNumber: number, projectId: number, clause: ContractAddendumClause) => {
        return { type: actionTypes.UPDATE_CONTRACT_CLAUSE, payload: { clientNumber, projectId, clause } };
    },
    clearClauses: () => {
        return { type: actionTypes.CLEAR_CLAUSES };
    },
    clearError: () => {
        return { type: actionTypes.CLEAR_ERROR };
    }
};

// Reducer
const reducer: Reducer<ILeaseClauseState> = (state: ILeaseClauseState = initialState, action: Action): ILeaseClauseState => {
    switch (action.type) {
        case actionTypes.GET_CONTRACT_CLAUSES_SUCCESS: {
            const getContractClausesPayload = (action as { type: string; payload: Array<ContractAddendumClause> }).payload;
            return {
                ...state,
                contractClauses: getContractClausesPayload,
            };
        }
        case actionTypes.DELETE_LEASE_CLAUSE_ERROR: {
            const payload = (action as { type: string; payload: ErrorResponse }).payload;
            return {
                ...state,
                error: payload.message,
                errorCode: payload.messageCode
            };
        }
        case actionTypes.CLEAR_ERROR: {
            return {
                ...state,
                error: undefined,
                errorCode: undefined
            };
        }
        default:
            return state;
    }
};

export default reducer;

// Sagas
const sagas = {
    deleteLeaseClause: function* (action: {
        type: string;
        payload: { clientNumber: number; projectId: number; id: number; tractType: string };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Clauses.deleteContractClause, action.payload.clientNumber, action.payload.projectId, action.payload.id, jwt), delay(200)]);
        if (result instanceof ErrorResponse) {
            yield put(actionCreators.deleteLeaseClauseError(result));
        } else {
            yield putResolve(actionCreators.getContractClauses(action.payload.clientNumber, action.payload.projectId));
        }
    },

    getContractClauses: function* (action: { type: string; payload: { clientNumber: number; projectId: number } }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        const [result] = yield all([call(Api.Clauses.getContractClauses, action.payload.clientNumber, action.payload.projectId, jwt), delay(200)]);
        yield put(actionCreators.getContractClausesSuccess(result));
    },

    createContractClause: function* (action: {
        type: string;
        payload: { clientNumber: number; projectId: number; clause: ContractAddendumClause };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        yield call(Api.Clauses.createContractClause, action.payload.clientNumber, action.payload.projectId, action.payload.clause, jwt);
        yield putResolve(actionCreators.getContractClauses(action.payload.clientNumber, action.payload.projectId));    },

    updateContractClause: function* (action: {
        type: string;
        payload: { clientNumber: number; projectId: number; clause: ContractAddendumClause };
    }): unknown {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const jwt = yield select(authSelectors.jwt);
        yield call(Api.Clauses.updateContractClause, action.payload.clientNumber, action.payload.projectId, action.payload.clause, jwt);
        yield putResolve(actionCreators.getContractClauses(action.payload.clientNumber, action.payload.projectId));    },
};

// Watches
const watches = {
    deleteLeaseClause: function* () {
        yield takeEvery(actionTypes.DELETE_LEASE_CLAUSE, sagas.deleteLeaseClause);
    },
    getContractClauses: function* () {
        yield takeEvery(actionTypes.GET_CONTRACT_CLAUSES, sagas.getContractClauses);
    },
    createContractClause: function* () {
        yield takeEvery(actionTypes.CREATE_CONTRACT_CLAUSE, sagas.createContractClause);
    },
    updateContractClause: function* () {
        yield takeEvery(actionTypes.UPDATE_CONTRACT_CLAUSE, sagas.updateContractClause);
    },
};

// Forks
export function* saga() {
    yield fork(watches.deleteLeaseClause);
    yield fork(watches.getContractClauses);
    yield fork(watches.createContractClause);
    yield fork(watches.updateContractClause);
}



// WEBPACK FOOTER //
// ./src/reducers/Clause.ts